import { supabase } from '../supabaseClient';

/**
 * Votable performing lineup for an event (excludes promoter brands), ordered by
 * billing. Rows: { artist_id, artist_name, normalized_name, billing_order, role,
 * performance_day, stage }.
 */
export async function fetchEventLineup(eventId) {
  const { data, error } = await supabase.rpc('event_lineup', { p_event_id: eventId });
  if (error) throw error;
  return data ?? [];
}

export async function fetchMyArtistVotes(eventId, userId) {
  const { data, error } = await supabase
    .from('user_artist_votes')
    .select('artist_id, performance_day')
    .eq('event_id', eventId)
    .eq('user_id', userId);
  if (error) throw error;
  return data ?? [];
}

export async function fetchMyEventAttendance(eventId, userId) {
  const { data, error } = await supabase
    .from('user_event_attendance')
    .select('attending_days')
    .eq('event_id', eventId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data?.attending_days ?? [];
}

/**
 * For each lineup artist, which of the viewer's MUTUAL follows voted for that
 * artist at this event. Returns Map-shaped object: { [artistId]: profile[] }.
 */
export async function fetchMutualVotersByArtist(eventId, userId) {
  if (!userId) return {};

  const [{ data: iFollow, error: e1 }, { data: followMe, error: e2 }] = await Promise.all([
    supabase.from('follows').select('following_id').eq('follower_id', userId),
    supabase.from('follows').select('follower_id').eq('following_id', userId),
  ]);
  if (e1) throw e1;
  if (e2) throw e2;

  const followingIds = new Set((iFollow ?? []).map((r) => r.following_id));
  const mutualIds = (followMe ?? []).map((r) => r.follower_id).filter((id) => followingIds.has(id));
  if (mutualIds.length === 0) return {};

  const { data: votes, error: votesError } = await supabase
    .from('user_artist_votes')
    .select('artist_id, user_id')
    .eq('event_id', eventId)
    .in('user_id', mutualIds);
  if (votesError) throw votesError;
  if (!votes || votes.length === 0) return {};

  const voterIds = [...new Set(votes.map((v) => v.user_id))];
  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, display_name, profile_photo_url')
    .in('id', voterIds);
  if (profilesError) throw profilesError;

  const profileById = new Map((profiles ?? []).map((p) => [p.id, p]));
  const byArtist = {};
  votes.forEach((vote) => {
    const profile = profileById.get(vote.user_id);
    if (!profile) return;
    if (!byArtist[vote.artist_id]) byArtist[vote.artist_id] = [];
    if (!byArtist[vote.artist_id].some((p) => p.id === profile.id)) {
      byArtist[vote.artist_id].push(profile);
    }
  });
  return byArtist;
}

/**
 * For each lineup artist, which of the given group members voted for that artist
 * at this event. Returns { [artistId]: profile[] }.
 */
export async function fetchGroupMemberVotersByArtist(eventId, memberIds, memberProfilesById) {
  if (!memberIds.length) return {};

  const { data: votes, error: votesError } = await supabase
    .from('user_artist_votes')
    .select('artist_id, user_id')
    .eq('event_id', eventId)
    .in('user_id', memberIds);
  if (votesError) throw votesError;
  if (!votes?.length) return {};

  const byArtist = {};
  votes.forEach((vote) => {
    const profile = memberProfilesById.get(vote.user_id);
    if (!profile) return;
    if (!byArtist[vote.artist_id]) byArtist[vote.artist_id] = [];
    if (!byArtist[vote.artist_id].some((p) => p.id === profile.id)) {
      byArtist[vote.artist_id].push(profile);
    }
  });
  return byArtist;
}

/**
 * Attending days for a set of users at one event, e.g. the day dots on the group
 * Members page. Same shape as fetchGroupMemberVotersByArtist: keyed by user id,
 * missing users simply have no key.
 */
export async function fetchAttendanceByUsers(eventId, userIds) {
  if (!userIds?.length) return {};

  const { data, error } = await supabase
    .from('user_event_attendance')
    .select('user_id, attending_days')
    .eq('event_id', eventId)
    .in('user_id', userIds);
  if (error) throw error;

  const byUser = {};
  (data ?? []).forEach((row) => {
    byUser[row.user_id] = row.attending_days ?? [];
  });
  return byUser;
}

/** Upsert the user's attending days for an event. */
export async function saveEventAttendanceMutationFn({ eventId, userId, attendingDays }) {
  const { error } = await supabase
    .from('user_event_attendance')
    .upsert(
      { event_id: eventId, user_id: userId, attending_days: attendingDays },
      { onConflict: 'user_id,event_id' },
    );
  if (error) throw error;
  return { eventId, attendingDays };
}

/**
 * Reconcile the user's votes for an event against the selected set.
 * `selections` = [{ artistId, performanceDay }]. Deletes unselected, upserts new.
 */
export async function saveArtistVotesMutationFn({ eventId, userId, selections }) {
  const keyOf = (artistId, day) => `${artistId}|${day ?? ''}`;
  const selectedKeys = new Set(selections.map((s) => keyOf(s.artistId, s.performanceDay)));

  const existing = await fetchMyArtistVotes(eventId, userId);
  const existingKeys = new Set(existing.map((v) => keyOf(v.artist_id, v.performance_day)));

  const toInsert = selections.filter((s) => !existingKeys.has(keyOf(s.artistId, s.performanceDay)));
  const toDelete = existing.filter((v) => !selectedKeys.has(keyOf(v.artist_id, v.performance_day)));

  if (toInsert.length > 0) {
    const { error } = await supabase.from('user_artist_votes').upsert(
      toInsert.map((s) => ({
        event_id: eventId,
        user_id: userId,
        artist_id: s.artistId,
        performance_day: s.performanceDay ?? null,
      })),
      { onConflict: 'user_id,artist_id,event_id,performance_day' },
    );
    if (error) throw error;
  }

  for (const vote of toDelete) {
    let query = supabase
      .from('user_artist_votes')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .eq('artist_id', vote.artist_id);
    query =
      vote.performance_day === null
        ? query.is('performance_day', null)
        : query.eq('performance_day', vote.performance_day);
    const { error } = await query;
    if (error) throw error;
  }

  return { eventId, voteCount: selections.length };
}

/** Lowercase letters and digits only, so "Ms. Lauryn Hill" is found in "Ms Lauryn Hill & Wyclef Jean". */
const squash = (t) =>
  String(t || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

/**
 * The headliners' Spotify artists for each event - the feed tiles' play button (Sam, 2026-10-27), up to four, in billing
 * order: everyone billed as headliner, plus a support act the event's own title names - co-billed shows store the second
 * name as support ("Ms Lauryn Hill & Wyclef Jean": Wyclef is support; "Jimmy Eat World + The Format"). Two or more play
 * as tabs in the now-playing bar, like a b2b. One query for a page of events; events Spotify knows nobody on are left out
 * (on 2026-10-27, 890 of the next 60 days' 1,169 events had someone).
 */
export async function fetchEventHeadliners(eventIds) {
  if (!eventIds?.length) return {};
  const { data, error } = await supabase
    .from('event_artists')
    .select(
      'event_id, billing_order, role, artists!inner(spotify_artist_id, name, spotify_image_url), events!inner(event_name)',
    )
    .in('event_id', eventIds)
    .in('role', ['headliner', 'support'])
    .not('artists.spotify_artist_id', 'is', null);
  if (error) throw error;
  const byEvent = {};
  for (const row of [...(data ?? [])].sort(
    (x, y) => (x.billing_order ?? 999) - (y.billing_order ?? 999),
  )) {
    const named =
      row.role === 'headliner' || squash(row.events?.event_name).includes(squash(row.artists.name));
    if (!named) continue;
    const list = (byEvent[row.event_id] ??= []);
    if (list.length < 4 && !list.some((a) => a.spotifyId === row.artists.spotify_artist_id)) {
      list.push({
        spotifyId: row.artists.spotify_artist_id,
        name: row.artists.name,
        image: row.artists.spotify_image_url ?? null,
      });
    }
  }
  return byEvent;
}
